import { TRANSPARENT_OPTIONS } from '@/constants/enum';
import { validateHexColor } from '@/helpers';
import { handleSetSaveBar, saveBarSelector } from '@/redux/slice/savebar.slice';
import { ColorError } from '@/types/components/widget';
import type { HSBAColor, RGBAColor } from '@shopify/polaris';
import {
  BlockStack,
  Button,
  Checkbox,
  ColorPicker,
  hexToRgb,
  hsbToRgb,
  Popover,
  rgbToHex,
  rgbToHsb,
  TextField,
} from '@shopify/polaris';
import { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ColorBox } from '../../styled';

export default function InputColorPicker({
  label,
  colorKey,
  error,
  colorValue,
  onChange,
  setError,
  disabled = false,
}: {
  label: string;
  colorKey: string;
  error?: ColorError;
  colorValue: RGBAColor;
  onChange: (color: RGBAColor) => void;
  setError?: (error: ColorError) => void;
  disabled?: boolean;
}) {
  const saveBar = useSelector(saveBarSelector);
  const dispatch = useDispatch();
  const [textColor, setTextColor] = useState<string>(rgbToHex(colorValue));
  const [popoverActive, setPopoverActive] = useState(false);
  const togglePopoverActive = useCallback(() => setPopoverActive((c) => !c), []);
  const handleColorChange = (newColor: HSBAColor) => {
    onChange({ ...hsbToRgb(newColor), alpha: newColor.alpha });
    const hexColor = rgbToHex(hsbToRgb(newColor));
    setTextColor(hexColor);
  };
  const handleTextColorChange = (newHexColor: string) => {
    setTextColor(newHexColor);

    if (!newHexColor || !validateHexColor(newHexColor)) {
      // setError({ ...error, error: 'Invalid color', isShow: widgetState.isChecking });
      const rgb = hexToRgb('#000000');
      onChange({ ...rgb, alpha: colorValue.alpha });
    } else if (validateHexColor(newHexColor)) {
      setError?.({ ...error, error: '', isShow: false });
      const rgb = hexToRgb(newHexColor);
      if (Object.values(rgb).includes(NaN)) return;
      onChange({ ...rgb, alpha: colorValue.alpha });
    } else {
      // setError({ ...error, error: 'Invalid color', isShow: true });
      const rgb = hexToRgb('#000000');
      onChange({ ...rgb, alpha: colorValue.alpha });
    }
  };

  const activator = (
    <Button onClick={togglePopoverActive} disabled={disabled}>
      {/* @ts-ignore */}
      <ColorBox colorValue={colorValue} />
    </Button>
  );

  useEffect(() => {
    if (saveBar.isDiscard) {
      setTextColor(rgbToHex(colorValue));
      dispatch(handleSetSaveBar({ isDiscard: false }));
    }
  }, [colorValue, dispatch, saveBar.isDiscard]);

  return (
    <TextField
      label={label}
      type="text"
      value={textColor.toUpperCase()}
      onChange={handleTextColorChange}
      onBlur={() => setTextColor(rgbToHex(colorValue).trim())}
      autoComplete="off"
      error={error?.isShow ? error?.error : undefined}
      name={colorKey}
      disabled={disabled}
      connectedRight={
        <Popover sectioned active={popoverActive} activator={activator} onClose={togglePopoverActive}>
          <Popover.Pane sectioned>
            <BlockStack gap="200">
              <ColorPicker onChange={handleColorChange} color={{ ...rgbToHsb(colorValue), alpha: colorValue.alpha }} allowAlpha />
              <Checkbox
                label="Transparent"
                checked={colorValue.alpha === TRANSPARENT_OPTIONS.TRANSPARENT}
                onChange={(checked) => {
                  onChange({ ...colorValue, alpha: checked ? TRANSPARENT_OPTIONS.TRANSPARENT : TRANSPARENT_OPTIONS.OPAQUE });
                }}
              />
            </BlockStack>
          </Popover.Pane>
        </Popover>
      }
    />
  );
}
